/* eslint-disable camelcase */
/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { useEffect, useState } from 'react';

import type { PeerReviewAi_UserFeedbackChoice } from '__generated__/graphql-types';

import { Button, Typography2 } from '@coursera/cds-core';
import { ThumbsDownFilledIcon, ThumbsDownIcon, ThumbsUpFilledIcon, ThumbsUpIcon } from '@coursera/cds-icons';

import { useAiFeedbackRatingContext } from 'bundles/peer/components/ai-grading/AiFeedbackRatingProvider';

import _t from 'i18n!nls/peer';

const styles = {
  container: () => css`
    display: flex;
    align-items: center;
    gap: var(--cds-spacing-200);
    justify-content: flex-end;
    margin-top: var(--cds-spacing-300);
  `,
  button: () => css`
    padding: 0;
  `,
};

type Props = {
  reviewId: string;
  reviewPartId: string;
};

const RateAiFeedback = ({ reviewId, reviewPartId }: Props) => {
  const [currentFeedbackChoice, setCurrentFeedbackChoice] = useState<PeerReviewAi_UserFeedbackChoice | null>(null);

  const { userFeedbackBySubmission, actions } = useAiFeedbackRatingContext();

  useEffect(() => {
    // if there's any user feedback on any ai comments available, then apply the state updates
    if (userFeedbackBySubmission.reviewPartUserFeedbacksByReviewId?.[reviewId]?.length) {
      const reviewFeedback = userFeedbackBySubmission.reviewPartUserFeedbacksByReviewId[reviewId].find(
        (reviewPart) => reviewPart.reviewPartId === reviewPartId
      );
      setCurrentFeedbackChoice(reviewFeedback?.feedbackChoice || null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userFeedbackBySubmission.reviewPartUserFeedbacksByReviewId]);

  /**
   * Attempts to save user feedback input
   *
   * @param feedbackInput
   */
  const handleFeedbackOnClick = async (feedbackInput: PeerReviewAi_UserFeedbackChoice) => {
    // if the user feedback choice is the same as the current one, this means it's unsetting, so set to null
    // otherwise set it to the one chosen
    const feedbackToUpdate = currentFeedbackChoice === feedbackInput ? null : feedbackInput;

    // update state so the change is more instant to the user, then apply the mutation
    setCurrentFeedbackChoice(feedbackToUpdate);
    actions.saveUserFeedback(feedbackToUpdate, reviewId, reviewPartId);
  };

  const buttonsAreDisabled = userFeedbackBySubmission.loading || actions.saveUserFeedbackLoading;

  return (
    <div css={styles.container}>
      <Typography2 component="p" variant="bodySecondary" color="supportText">
        {_t('Rate this feedback:')}
      </Typography2>
      <Button
        variant="ghost"
        css={styles.button}
        onClick={() => handleFeedbackOnClick('LIKE')}
        aria-label={_t('Like')}
        data-testid="ai-feedback-like"
        disabled={buttonsAreDisabled}
        title={_t('Like')}
      >
        {currentFeedbackChoice === 'LIKE' ? (
          <ThumbsUpFilledIcon color="interactive" size="small" />
        ) : (
          <ThumbsUpIcon color="support" size="small" />
        )}
      </Button>
      <Button
        variant="ghost"
        css={styles.button}
        onClick={() => handleFeedbackOnClick('DISLIKE')}
        aria-label={_t('Dislike')}
        data-testid="ai-feedback-dislike"
        disabled={buttonsAreDisabled}
        title={_t('Dislike')}
      >
        {currentFeedbackChoice === 'DISLIKE' ? (
          <ThumbsDownFilledIcon color="interactive" size="small" />
        ) : (
          <ThumbsDownIcon color="support" size="small" />
        )}
      </Button>
    </div>
  );
};

export default RateAiFeedback;
